import type { Workflow } from "@/types/resources";
import { useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

interface MainWorkflowState {
  workflows: Workflow[];
  setWorkflow: (workflow: Workflow[]) => void;
  isAddRulePopupOpen: boolean;
  openAddRulePopup: (folderId?: string) => void;
  closeAddRulePopup: () => void;
  isDeleteGroupPopupOpen: boolean;
  openDeleteGroupPopup: (workflowId?: string) => void;
  closeDeleteGroupPopup: () => void;
  isCloneGroupPopupOpen: boolean;
  openCloneGroupPopup: (workflowId: string) => void;
  closeCloneGroupPopup: () => void;
  selectedWorkflowId: string;
  selectedFolderId: string | undefined;
}

interface ScrollPositionState {
  workflowListScrollPosition: number;
  setWorkflowListScrollPosition: (position: number) => void;
}

type WorkflowState = MainWorkflowState & ScrollPositionState;

const useMainStore = create<MainWorkflowState>()(
  devtools(
    persist(
      (set) => ({
        workflows: [],
        setWorkflow: (workflow: Workflow[]) =>
          set((state: MainWorkflowState) => ({
            ...state,
            workflows: workflow,
          })),
        isAddRulePopupOpen: false,
        openAddRulePopup: (folderId?: string) =>
          set((state: MainWorkflowState) => ({
            ...state,
            isAddRulePopupOpen: true,
            selectedFolderId: folderId,
          })),
        closeAddRulePopup: () =>
          set((state: MainWorkflowState) => ({
            ...state,
            isAddRulePopupOpen: false,
            selectedFolderId: undefined,
          })),
        isDeleteGroupPopupOpen: false,
        openDeleteGroupPopup: (workflowId?: string) =>
          set((state: MainWorkflowState) => ({
            ...state,
            isDeleteGroupPopupOpen: true,
            selectedWorkflowId: workflowId,
          })),
        closeDeleteGroupPopup: () =>
          set((state: MainWorkflowState) => ({
            ...state,
            isDeleteGroupPopupOpen: false,
          })),
        isCloneGroupPopupOpen: false,
        openCloneGroupPopup: (workflowId: string) =>
          set((state: MainWorkflowState) => ({
            ...state,
            isCloneGroupPopupOpen: true,
            selectedWorkflowId: workflowId,
          })),
        closeCloneGroupPopup: () =>
          set((state: MainWorkflowState) => ({
            ...state,
            isCloneGroupPopupOpen: false,
          })),
        selectedWorkflowId: "",
        selectedFolderId: undefined,
      }),
      {
        name: "workflow-main-storage",
        storage: createJSONStorage(() => localStorage),
      }
    )
  )
);

const useScrollStore = create<ScrollPositionState>()(
  devtools(
    persist(
      (set) => ({
        workflowListScrollPosition: 0,
        setWorkflowListScrollPosition: (position: number) =>
          set((state: ScrollPositionState) => ({
            ...state,
            workflowListScrollPosition: position,
          })),
      }),
      {
        name: "workflow-scroll-storage",
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
);

const useWorkflowStore = () => {
  const mainState = useMainStore();
  const scrollState = useScrollStore();

  return useMemo(() => {
    return {
      ...mainState,
      ...scrollState,
    } as WorkflowState;
  }, [mainState, scrollState]);
};

export default useWorkflowStore;
